import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Grid,
  Divider,
  Alert,
  AppBar,
  Toolbar,
  IconButton,
  Card,
  CardContent,
  Chip,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableRow
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Download as DownloadIcon,
  Description as ContractIcon,
  Work as WorkIcon,
  Euro as EuroIcon,
  CalendarMonth as CalendarIcon,
  LocationOn as LocationIcon,
  CheckCircle as ActiveIcon,
  Cancel as ExpiredIcon,
  Schedule as PendingIcon,
  Visibility as ViewIcon,
  Close as CloseIcon,
  AccessTime as ClockIcon
} from '@mui/icons-material';
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineDot,
  TimelineConnector,
  TimelineContent
} from '@mui/lab'
import api from '../services/api';

const EmployeeContracts = () => {
  const navigate = useNavigate();
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedContract, setSelectedContract] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const loggedIn = localStorage.getItem('employeeLoggedIn');
    if (!loggedIn) {
      navigate('/employee/login');
      return;
    }
    fetchContracts();
  }, [navigate]);

  const fetchContracts = async () => {
    try {
      const response = await api.get('/employee/contracts');
      setContracts(response.data.contracts || []);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching contracts:', error);
      setContracts([]);
      setLoading(false);
    }
  };

  const handleView = (contract) => {
    setSelectedContract(contract);
    setOpenDialog(true);
  };

  const handleDownload = async (contractId) => {
    try {
      const response = await api.get(`/employee/contracts/${contractId}/download`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `contract-${contractId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      setError('Contract download will be available soon');
    }
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'active': return 'success';
      case 'expired': return 'error';
      case 'terminated': return 'error';
      case 'pending': return 'warning';
      default: return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch(status) {
      case 'active': return <ActiveIcon />;
      case 'expired': return <ExpiredIcon />;
      case 'terminated': return <ExpiredIcon />;
      case 'pending': return <PendingIcon />;
      default: return <ContractIcon />;
    }
  };

  const formatCurrency = (amount, currency = 'EUR') => {
    return new Intl.NumberFormat('lt-LT', {
      style: 'currency',
      currency: currency
    }).format(amount || 0);
  };

  const activeContract = contracts.find(c => c.status === 'active');

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      {/* App Bar */}
      <AppBar position="fixed" sx={{ bgcolor: '#1976d2' }}>
        <Toolbar>
          <IconButton color="inherit" edge="start" onClick={() => navigate('/employee/dashboard')}>
            <BackIcon />
          </IconButton>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            My Contracts
          </Typography>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ pt: 10, pb: 3 }}>
        {/* Alerts */}
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

        {/* Active Contract Highlight */}
        {activeContract && (
          <Paper sx={{ p: 3, mb: 3, bgcolor: '#e3f2fd', border: '2px solid #1976d2' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <WorkIcon sx={{ color: '#1976d2', mr: 2, fontSize: 30 }} />
              <Typography variant="h6">Current Active Contract</Typography>
            </Box>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Stack spacing={1}>
                  <Typography variant="subtitle2" color="text.secondary">Position</Typography>
                  <Typography variant="h6">{activeContract.position_title}</Typography>
                  
                  <Typography variant="subtitle2" color="text.secondary" sx={{ pt: 1 }}>Contract Type</Typography>
                  <Typography>{activeContract.contract_type}</Typography>
                  
                  <Typography variant="subtitle2" color="text.secondary" sx={{ pt: 1 }}>Location</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <LocationIcon sx={{ fontSize: 18, mr: 0.5 }} />
                    <Typography>{activeContract.work_location}</Typography>
                  </Box>
                </Stack>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Stack spacing={1}>
                  <Typography variant="subtitle2" color="text.secondary">Duration</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <CalendarIcon sx={{ fontSize: 18, mr: 0.5 }} />
                    <Typography>
                      {new Date(activeContract.start_date).toLocaleDateString()} - 
                      {activeContract.end_date ? 
                        new Date(activeContract.end_date).toLocaleDateString() : 
                        'Ongoing'}
                    </Typography>
                  </Box>
                  
                  <Typography variant="subtitle2" color="text.secondary" sx={{ pt: 1 }}>Salary</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <EuroIcon sx={{ fontSize: 18, mr: 0.5 }} />
                    <Typography variant="h6">
                      {formatCurrency(activeContract.salary_amount, activeContract.salary_currency)}
                      <Typography component="span" variant="body2" sx={{ ml: 1 }}>
                        / {activeContract.salary_type || 'month'}
                      </Typography>
                    </Typography>
                  </Box>
                  
                  <Typography variant="subtitle2" color="text.secondary" sx={{ pt: 1 }}>Working Hours</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <ClockIcon sx={{ fontSize: 18, mr: 0.5 }} />
                    <Typography>{activeContract.working_hours_per_week || 40} hours/week</Typography>
                  </Box>
                </Stack>
              </Grid>
              
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                  <Button 
                    variant="contained" 
                    startIcon={<ViewIcon />}
                    onClick={() => handleView(activeContract)}
                  >
                    View Details
                  </Button>
                  <Button 
                    variant="outlined" 
                    startIcon={<DownloadIcon />}
                    onClick={() => handleDownload(activeContract.id)}
                  >
                    Download PDF
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        )}

        {/* Contract History */}
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>Contract History</Typography>
          <Divider sx={{ mb: 3 }} />
          
          {contracts.length > 0 ? (
            <Timeline position="alternate">
              {contracts.map((contract, index) => (
                <TimelineItem key={contract.id}>
                  <TimelineSeparator>
                    <TimelineDot 
                      color={getStatusColor(contract.status)}
                      variant={contract.status === 'active' ? 'filled' : 'outlined'}
                    >
                      {getStatusIcon(contract.status)}
                    </TimelineDot>
                    {index < contracts.length - 1 && <TimelineConnector />}
                  </TimelineSeparator>
                  <TimelineContent>
                    <Card sx={{ cursor: 'pointer' }} onClick={() => handleView(contract)}>
                      <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <Box>
                            <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                              {contract.position_title}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {contract.contract_type} • {contract.work_location}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {new Date(contract.start_date).toLocaleDateString()} - 
                              {contract.end_date ? 
                                new Date(contract.end_date).toLocaleDateString() : 
                                'Present'}
                            </Typography>
                          </Box>
                          <Chip 
                            label={contract.status}
                            size="small"
                            color={getStatusColor(contract.status)}
                          />
                        </Box>
                      </CardContent>
                    </Card>
                  </TimelineContent>
                </TimelineItem>
              ))}
            </Timeline>
          ) : (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <ContractIcon sx={{ fontSize: 60, color: '#ccc', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No Contracts Found
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Your employment contracts will appear here
              </Typography>
            </Box>
          )}
        </Paper>
      </Container>

      {/* Contract Details Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Contract Details</Typography>
            <IconButton onClick={() => setOpenDialog(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {selectedContract && (
            <Table>
              <TableBody>
                <TableRow>
                  <TableCell sx={{ fontWeight: 500 }}>Contract Number</TableCell>
                  <TableCell>{selectedContract.contract_number}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: 500 }}>Position</TableCell>
                  <TableCell>{selectedContract.position_title}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: 500 }}>Contract Type</TableCell>
                  <TableCell>{selectedContract.contract_type}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: 500 }}>Location</TableCell>
                  <TableCell>{selectedContract.work_location}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: 500 }}>Start Date</TableCell>
                  <TableCell>{new Date(selectedContract.start_date).toLocaleDateString()}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: 500 }}>End Date</TableCell>
                  <TableCell>
                    {selectedContract.end_date ? 
                      new Date(selectedContract.end_date).toLocaleDateString() : 
                      'Indefinite'}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: 500 }}>Salary</TableCell>
                  <TableCell>
                    {formatCurrency(selectedContract.salary_amount, selectedContract.salary_currency)}
                    / {selectedContract.salary_type || 'month'}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: 500 }}>Working Hours</TableCell>
                  <TableCell>{selectedContract.working_hours_per_week || 40} hours/week</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: 500 }}>Vacation Days</TableCell>
                  <TableCell>{selectedContract.vacation_days || 20} days/year</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: 500 }}>Notice Period</TableCell>
                  <TableCell>{selectedContract.notice_period_days || 30} days</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: 500 }}>Status</TableCell>
                  <TableCell>
                    <Chip 
                      label={selectedContract.status}
                      color={getStatusColor(selectedContract.status)}
                      size="small"
                    />
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => handleDownload(selectedContract?.id)} startIcon={<DownloadIcon />}>
            Download PDF
          </Button>
          <Button onClick={() => setOpenDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EmployeeContracts;
